// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, AlertCircle } from "lucide-react";
import { trackLead } from "../lib/metaPixel";
import { generateMetaEventId } from "../lib/metaEvents";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Single event_id used by BOTH the browser Pixel "Lead" and the server
      // CAPI Lead so Meta dedupes on (event_id, event_name).
      const leadEventId = generateMetaEventId();
      await register(email, password, name, {
        meta_event_id: leadEventId,
        meta_event_source_url: window.location.href,
      });
      trackLead({ event_id: leadEventId, content_name: "registration" });
      toast.success("Account created");
      navigate("/portal", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleSignup = () => {
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-10 justify-center" data-testid="register-logo">
          <div className="leading-none">
            <div className="flex items-baseline gap-0.5">
              <span className="font-heading text-2xl font-black text-[#212121] tracking-tight">DFTB</span>
              <span className="font-heading text-2xl font-black text-[#28BBE3] tracking-tight">26</span>
            </div>
            <div className="text-center text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mt-1">Virtual</div>
          </div>
        </Link>

        <div className="rounded-3xl bg-white ring-1 ring-black/5 p-8 sm:p-10 shadow-xl shadow-slate-200/40">
          {/* Heads-up banner: most visitors land here by mistake */}
          <div className="flex gap-3 rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-4 mb-6" data-testid="register-warning">
            <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-slate-800 leading-relaxed">
              <span className="font-semibold">Heads up — you need a ticket first.</span>{" "}
              Creating an account here does <span className="font-semibold">not</span> give you access to the virtual conference.
              Tickets are purchased on the homepage; your login is then emailed to you automatically.
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              onClick={() => navigate("/#tickets")}
              className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white py-6 font-semibold"
              data-testid="register-buy-ticket-btn"
            >
              Buy a ticket
              <ArrowRight className="ml-2" size={16} />
            </Button>
            <Button
              onClick={() => navigate("/login")}
              variant="outline"
              className="rounded-full border-slate-300 text-slate-800 py-6 hover:bg-slate-50"
              data-testid="register-signin-btn"
            >
              I already have a ticket
            </Button>
          </div>

          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => setShowForm((s) => !s)}
              className="text-xs uppercase tracking-[0.18em] text-slate-400 hover:text-slate-600 font-semibold underline"
              data-testid="register-toggle-form"
            >
              {showForm ? "Hide sign-up form" : "Sign up without a ticket"}
            </button>
          </div>

          {showForm && (
            <div className="mt-6 pt-6 border-t border-slate-100">
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Create account</div>
              <h1 className="mt-2 font-heading text-2xl tracking-tighter font-medium text-slate-900">
                Join DFTB26.
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Already have an account?{" "}
                <Link to="/login" className="text-[#28BBE3] hover:underline font-semibold" data-testid="register-login-link">
                  Sign in
                </Link>
              </p>

              <Button onClick={googleSignup} variant="outline"
                className="mt-7 w-full rounded-full border-slate-300 text-slate-800 py-6 hover:bg-slate-50"
                data-testid="register-google-btn">
                <svg className="mr-2" width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.6 6 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.6 7 29.6 5 24 5 16.3 5 9.6 9.1 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.2-5.5l-6.6-5.4c-2.1 1.5-4.8 2.4-7.6 2.4-5.3 0-9.7-3.3-11.3-8L6.1 32C9.3 38.4 16.1 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.5-4.5 5.9l6.6 5.4C40 36 44 30.5 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
                Continue with Google
              </Button>

              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">or</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Full name</Label>
                  <Input id="name" required value={name} onChange={(e) => setName(e.target.value)}
                    className="mt-2 rounded-2xl border-slate-200 bg-white py-6"
                    placeholder="Dr. Jane Doe" data-testid="register-name-input" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Email</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 rounded-2xl border-slate-200 bg-white py-6"
                    placeholder="you@hospital.org" data-testid="register-email-input" />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Password</Label>
                  <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    className="mt-2 rounded-2xl border-slate-200 bg-white py-6"
                    placeholder="At least 6 characters" data-testid="register-password-input" />
                </div>
                <Button type="submit" disabled={loading}
                  className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white py-6 font-semibold"
                  data-testid="register-submit-btn">
                  {loading ? "Creating account…" : "Create account"}
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </form>

              <p className="mt-6 text-xs text-slate-400 text-center">
                By creating an account you agree to our terms and privacy policy.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
